import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Descriptions,
  Badge,
  Tree,
  Switch,
  Tooltip,
  Divider,
  InputNumber,
  Checkbox
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  ApartmentOutlined
} from '@ant-design/icons';
import { 
  Permission, 
  CreatePermissionRequest, 
  UpdatePermissionRequest, 
  PermissionType, 
  PermissionCategory,
  PermissionTreeNode,
  CreatePermissionTreeRequest,
  PermissionTreeResponse
} from '../../types';
import { permissionApi } from '../../services/api';
import { usePermissionCheck, PermissionGuard, usePermissions } from '../../context/PermissionContext';
import { useTheme } from '../../context/ThemeContext';

const { Title } = Typography;
const { Option } = Select;

// 视图模式枚举
enum ViewMode {
  TABLE = 'table',
  TREE = 'tree'
}

const PermissionManagement: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionTree, setPermissionTree] = useState<PermissionTreeNode[]>([]);
  const [filteredPermissions, setFilteredPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.TREE); // 默认使用树型视图
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState<boolean>(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [detailPermission, setDetailPermission] = useState<Permission | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // 筛选条件状态
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    type: ''
  });

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { theme } = useTheme();
  const {
    canViewPermissions,
    canCreatePermissions,
    canEditPermissions,
    canDeletePermissions
  } = usePermissionCheck();
  const { loading: permissionLoading } = usePermissions(); // 获取权限加载状态

  // 权限分类选项
  const categoryOptions = [
    { label: '仪表盘', value: 'dashboard' },
    { label: '用户管理', value: 'user' },
    { label: '角色管理', value: 'role' },
    { label: '权限管理', value: 'permission' },
    { label: 'API管理', value: 'api' },
    { label: '日志管理', value: 'log' },
    { label: '系统管理', value: 'system' }
  ];

  // 权限类型选项
  const typeOptions = [
    { label: '模块权限', value: 'module' },
    { label: '页面权限', value: 'page' },
    { label: '操作权限', value: 'action' },
    { label: '按钮权限', value: 'button' }
  ];

  // 获取权限列表（表格模式）
  const fetchPermissions = async (
    page = currentPage,
    size = pageSize,
    name = filters.name,
    category = filters.category,
    type = filters.type
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: size,
        name,
        category,
        type,
      };
      const response = await permissionApi.getPermissions(params);
      const permissionList = response.data.permissions || [];
      setPermissions(permissionList);
      setTotal(response.data.total || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || '获取权限列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取权限树（树型模式）
  const fetchPermissionTree = async () => {
    try {
      setLoading(true);
      const response = await permissionApi.getPermissionTree();
      const treeData = response.data.tree || [];
      console.log('🔍 权限树数据:', treeData);
      console.log('🔍 第一个权限详情:', treeData[0]);
      
      // 调试：检查扁平化后的权限列表
      const flatList = flattenPermissionTree(treeData);
      console.log('🔍 扁平化权限列表:', flatList.map(p => ({
        id: p.id,
        name: p.name,
        code: p.code,
        level: p.level,
        sort: p.sort,
        parent_id: p.parent_id
      })));
      
      setPermissionTree(treeData);
    } catch (error: any) {
      console.error('获取权限树失败:', error);
      message.error(error.response?.data?.message || '获取权限树失败');
    } finally {
      setLoading(false);
    }
  };

  // 应用筛选条件
  const applyFilters = (permissionList: Permission[] = permissions) => {
    let filtered = [...permissionList];

    // 按权限名称筛选（模糊搜索）
    if (filters.name) {
      filtered = filtered.filter(permission => 
        permission.name.toLowerCase().includes(filters.name.toLowerCase()) ||
        permission.code.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    // 按分类筛选
    if (filters.category) {
      filtered = filtered.filter(permission => permission.category === filters.category);
    }

    // 按类型筛选
    if (filters.type) {
      filtered = filtered.filter(permission => permission.type === filters.type);
    }

    setFilteredPermissions(filtered);
  };

  // 将权限树扁平化为按层级排序的列表
  const flattenPermissionTree = (nodes: PermissionTreeNode[]): PermissionTreeNode[] => {
    const result: PermissionTreeNode[] = [];
    
    const traverse = (nodeList: PermissionTreeNode[]) => {
      // 按照level和sort排序
      const sortedNodes = [...nodeList].sort((a, b) => {
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        return a.sort - b.sort;
      });
      
      for (const node of sortedNodes) {
        result.push(node);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    
    traverse(nodes);
    return result;
  };

  // 构建父权限选项（用于选择父权限）
  const buildParentOption = (node: PermissionTreeNode): React.ReactNode => {
    const actualLevel = node.level || 0;
    
    // 创建层级指示器 - 根据实际层级显示
    const createLevelIndicator = (level: number) => {
      if (level === 0) return '';
      if (level === 1) return '├─ ';
      if (level === 2) return '│　├─ ';
      return '│　'.repeat(level - 1) + '├─ ';
    };
    
    const levelIndicator = createLevelIndicator(actualLevel);
    
    const icon = node.icon && node.icon !== '' ? '📊' : 
                 node.type === 'module' ? '📁' :
                 node.type === 'page' ? '📄' :
                 node.type === 'action' ? '⚡' : '🔘';
    
    // 禁用当前编辑的权限及其所有子权限（防止循环引用）
    const isDisabled = editingPermission && (
      node.id === editingPermission.id ||
      isDescendantOf(node.id, editingPermission.id, permissionTree)
    );
    
    return (
      <Option 
        key={node.id}
        value={node.id} 
        disabled={isDisabled}
        title={`Level ${actualLevel}: ${node.name} (${node.code})`}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px',
          padding: '2px 0',
          opacity: isDisabled ? 0.5 : 1,
          fontSize: '13px'
        }}>
          {/* 层级指示器区域 - 固定宽度确保对齐 */}
          <span style={{ 
            minWidth: '60px',
            fontFamily: 'monospace',
            color: theme === 'dark' ? '#666' : '#999',
            fontSize: '11px'
          }}>
            {levelIndicator}
          </span>
          
          {/* 权限图标 */}
          <span style={{ fontSize: '14px', minWidth: '20px' }}>{icon}</span>
          
          {/* 权限名称 */}
          <span style={{ 
            fontWeight: actualLevel === 0 ? 600 : actualLevel === 1 ? 500 : 400,
            color: theme === 'dark' ? '#ffffff' : '#000000',
            minWidth: '80px'
          }}>
            {node.name}
          </span>
          
          {/* 权限类型标签 */}
          <Tag 
            color={getTypeColor(node.type)} 
            style={{ 
              margin: 0, 
              fontSize: '9px', 
              lineHeight: '14px', 
              padding: '0 3px',
              borderRadius: '2px',
              minWidth: '32px',
              textAlign: 'center'
            }}
          >
            {getTypeText(node.type)}
          </Tag>
          
          {/* 权限代码 */}
          <code style={{ 
            background: theme === 'dark' ? '#2f2f2f' : '#f0f0f0', 
            color: theme === 'dark' ? '#888' : '#666',
            padding: '1px 4px', 
            borderRadius: '2px',
            fontSize: '10px',
            fontFamily: 'Monaco, Consolas, monospace',
            minWidth: '100px'
          }}>
            {node.code}
          </code>
          
          {/* 层级和排序信息 */}
          <span style={{ 
            color: theme === 'dark' ? '#666' : '#999', 
            fontSize: '9px',
            marginLeft: 'auto',
            fontWeight: 'normal',
            fontFamily: 'monospace'
          }}>
            L{actualLevel}#{node.sort}
          </span>
        </div>
      </Option>
    );
  };

  // 检查是否为某个权限的后代（用于防止循环引用）
  const isDescendantOf = (nodeId: number, ancestorId: number, tree: PermissionTreeNode[]): boolean => {
    const findNodePath = (targetId: number, currentTree: PermissionTreeNode[], path: number[] = []): number[] | null => {
      for (const node of currentTree) {
        const currentPath = [...path, node.id];
        if (node.id === targetId) {
          return currentPath;
        }
        if (node.children) {
          const result = findNodePath(targetId, node.children, currentPath);
          if (result) return result;
        }
      }
      return null;
    };

    const nodePath = findNodePath(nodeId, tree);
    return nodePath ? nodePath.includes(ancestorId) : false;
  };

  // 处理筛选条件变化
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    fetchPermissions(1, pageSize, key === 'name' ? value : filters.name, key === 'category' ? value : filters.category, key === 'type' ? value : filters.type);
  };

  // 重置筛选条件
  const resetFilters = () => {
    setFilters({ name: '', category: '', type: '' });
    setCurrentPage(1);
    fetchPermissions(1, pageSize, '', '', '');
  };

  // 获取权限名称选项（用于下拉搜索）
  const getPermissionNameOptions = () => {
    const names = permissions.map(p => p.name);
    const uniqueNames = Array.from(new Set(names));
    return uniqueNames.map(name => ({ label: name, value: name }));
  };

  // 切换视图模式
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === ViewMode.TREE) {
      fetchPermissionTree();
    } else {
      fetchPermissions();
    }
  };

  // 刷新数据
  const handleRefresh = () => {
    if (viewMode === ViewMode.TREE) {
      fetchPermissionTree();
    } else {
      fetchPermissions();
    }
  };

  useEffect(() => {
    // 等待权限加载完成后再初始化数据
    if (!permissionLoading && canViewPermissions()) {
      // 根据当前视图模式加载对应数据
      if (viewMode === ViewMode.TREE) {
        fetchPermissionTree();
      } else {
        fetchPermissions();
      }
    }
  }, [permissionLoading, viewMode]); // 使用权限加载状态作为依赖

  useEffect(() => {
    fetchPermissions(currentPage, pageSize, filters.name, filters.category, filters.type);
  }, [currentPage, pageSize, filters.name, filters.category, filters.type]);

  // 权限类型标签颜色
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'module': return 'purple';
      case 'page': return 'blue';
      case 'action': return 'green';
      case 'button': return 'orange';
      default: return 'default';
    }
  };

  // 权限类型标签文本
  const getTypeText = (type: string) => {
    switch (type) {
      case 'module': return '模块';
      case 'page': return '页面';
      case 'action': return '操作';
      case 'button': return '按钮';
      default: return '未知';
    }
  };

  // 权限分类标签颜色
  const getCategoryColor = (category: string) => {
    const colors = ['purple', 'cyan', 'magenta', 'gold', 'lime', 'geekblue', 'volcano'];
    const index = category.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // 将权限树节点转换为Antd Tree组件需要的数据格式
  const convertToTreeData = (nodes: PermissionTreeNode[]): DataNode[] => {
    return nodes.map(node => ({
      key: node.id.toString(),
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>{node.icon && node.icon !== '' ? '📊' : '📄'}</span>
            <span>{node.name}</span>
                         <Tag color={getTypeColor(node.type)}>
               {getTypeText(node.type)}
             </Tag>
            <code style={{ 
              background: '#f5f5f5', 
              padding: '2px 6px', 
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              {node.code}
            </code>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewTreeDetail(node);
              }}
            />
            <PermissionGuard permission="permission.edit">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditTreeNode(node);
                }}
              />
            </PermissionGuard>
            <PermissionGuard permission="permission.delete">
                             <Popconfirm
                 title="确定要删除这个权限吗？"
                 description="删除后将无法恢复，请谨慎操作。"
                 onConfirm={() => handleDelete(node.id)}
                 okText="确定"
                 cancelText="取消"
               >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </PermissionGuard>
          </div>
        </div>
      ),
      children: node.children && node.children.length > 0 ? convertToTreeData(node.children) : undefined,
      isLeaf: node.is_leaf,
    }));
  };

  // 查看树节点详情
  const handleViewTreeDetail = (node: PermissionTreeNode) => {
    // 转换为Permission格式
    const permission: Permission = {
      id: node.id,
      code: node.code,
      name: node.name,
      category: node.category || '',
      description: node.description,
      type: node.type,
      parent_id: node.parent_id,
      path: node.path,
      level: node.level,
      sort: node.sort,
      icon: node.icon,
      is_leaf: node.is_leaf,
      is_enabled: node.is_enabled,
      created_at: node.created_at,
      updated_at: node.updated_at
    };
    setDetailPermission(permission);
    setIsDetailModalVisible(true);
  };

  // 编辑树节点
  const handleEditTreeNode = (node: PermissionTreeNode) => {
    console.log('🔍 编辑权限节点:', node);
    console.log('🔍 权限parent_id:', node.parent_id);
    console.log('🔍 当前权限树数量:', permissionTree.length);
    
    // 转换为Permission格式
    const permission: Permission = {
      id: node.id,
      code: node.code,
      name: node.name,
      category: node.category || '',
      description: node.description,
      type: node.type,
      parent_id: node.parent_id,
      path: node.path,
      level: node.level,
      sort: node.sort,
      icon: node.icon,
      is_leaf: node.is_leaf,
      is_enabled: node.is_enabled,
      created_at: node.created_at,
      updated_at: node.updated_at
    };
    setEditingPermission(permission);
    
    const formValues = {
      code: permission.code,
      name: permission.name,
      category: permission.category,
      description: permission.description,
      type: permission.type,
      parent_id: permission.parent_id,
      level: permission.level,
      sort: permission.sort,
      icon: permission.icon,
      path: permission.path,
      is_leaf: permission.is_leaf,
      is_enabled: permission.is_enabled
    };
    console.log('🔍 表单设置值:', formValues);
    
    editForm.setFieldsValue(formValues);
    setIsEditModalVisible(true);
  };

  // 创建权限
  const handleCreate = async (values: CreatePermissionRequest) => {
    try {
      await permissionApi.createPermission(values);
      message.success('权限创建成功');
      setIsCreateModalVisible(false);
      createForm.resetFields();
      handleRefresh();
    } catch (error: any) {
      console.error('创建权限失败:', error);
      message.error(error.response?.data?.message || '创建权限失败');
    }
  };

  // 编辑权限
  const handleEdit = async (values: UpdatePermissionRequest) => {
    if (!editingPermission) return;
    
    try {
      // 构建完整的更新请求数据，包含层级结构字段
      const updateData: UpdatePermissionRequest = {
        code: values.code,
        name: values.name,
        category: values.category,
        description: values.description,
        type: values.type,
        parent_id: values.parent_id,
        level: values.level,
        sort: values.sort,
        icon: values.icon,
        path: values.path,
        is_leaf: values.is_leaf,
        is_enabled: values.is_enabled
      };

      await permissionApi.updatePermission(editingPermission.id, updateData);
      message.success('权限更新成功');
      setIsEditModalVisible(false);
      setEditingPermission(null);
      editForm.resetFields();
      handleRefresh();
    } catch (error: any) {
      console.error('更新权限失败:', error);
      message.error(error.response?.data?.message || '更新权限失败');
    }
  };

  // 删除权限
  const handleDelete = async (id: number) => {
    try {
      await permissionApi.deletePermission(id);
      message.success('权限删除成功');
      handleRefresh();
    } catch (error: any) {
      console.error('删除权限失败:', error);
      message.error(error.response?.data?.message || '删除权限失败');
    }
  };

  // 查看权限详情
  const handleViewDetail = async (permission: Permission) => {
    setDetailPermission(permission);
    setIsDetailModalVisible(true);
  };

  // 编辑权限
  const handleEditClick = (permission: Permission) => {
    setEditingPermission(permission);
    editForm.setFieldsValue({
      code: permission.code,
      name: permission.name,
      category: permission.category,
      description: permission.description,
      type: permission.type,
      parent_id: permission.parent_id,
      level: permission.level,
      sort: permission.sort,
      icon: permission.icon,
      path: permission.path,
      is_leaf: permission.is_leaf,
      is_enabled: permission.is_enabled
    });
    setIsEditModalVisible(true);
  };

  // 表格列定义
  const columns: ColumnsType<Permission> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '权限代码',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      ellipsis: true,
    },
    {
      title: '权限名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => (
        <Tag color={getCategoryColor(category)}>{category}</Tag>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{getTypeText(type)}</Tag>
      ),
    },
    {
      title: '层级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level: number) => (
        <Badge count={level} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <PermissionGuard permission="permission.edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
            >
              编辑
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="permission.delete">
            <Popconfirm
              title="确定要删除这个权限吗？"
              description="删除后将无法恢复，请谨慎操作。"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  // 权限检查失败时的展示
  if (!canViewPermissions()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看权限管理的权限</p>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>权限管理</Title>
          </Col>
          <Col>
            <Space>
              {/* 视图切换 */}
              <Space.Compact>
                <Tooltip title="树型视图">
                  <Button
                    icon={<ApartmentOutlined />}
                    type={viewMode === ViewMode.TREE ? 'primary' : 'default'}
                    onClick={() => handleViewModeChange(ViewMode.TREE)}
                  >
                    树型
                  </Button>
                </Tooltip>
                <Tooltip title="表格视图">
                  <Button
                    icon={<UnorderedListOutlined />}
                    type={viewMode === ViewMode.TABLE ? 'primary' : 'default'}
                    onClick={() => handleViewModeChange(ViewMode.TABLE)}
                  >
                    表格
                  </Button>
                </Tooltip>
              </Space.Compact>
              <Divider type="vertical" />
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                刷新
              </Button>
              <PermissionGuard permission="permission.create">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreateModalVisible(true)}
                >
                  新建权限
                </Button>
              </PermissionGuard>
            </Space>
          </Col>
        </Row>

        {/* 表格模式的筛选区域 */}
        {viewMode === ViewMode.TABLE && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: theme === 'dark' ? '#ffffff' : '#000000' }}>权限名称</label>
                  <Select
                    placeholder="请选择或输入权限名称"
                    value={filters.name || undefined}
                    onChange={(value) => handleFilterChange('name', value || '')}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                    style={{ width: '100%' }}
                    options={getPermissionNameOptions()}
                  />
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: theme === 'dark' ? '#ffffff' : '#000000' }}>权限分类</label>
                  <Select
                    placeholder="请选择分类"
                    value={filters.category || undefined}
                    onChange={(value) => handleFilterChange('category', value || '')}
                    allowClear
                    style={{ width: '100%' }}
                    options={categoryOptions}
                  />
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: theme === 'dark' ? '#ffffff' : '#000000' }}>权限类型</label>
                  <Select
                    placeholder="请选择类型"
                    value={filters.type || undefined}
                    onChange={(value) => handleFilterChange('type', value || '')}
                    allowClear
                    style={{ width: '100%' }}
                    options={typeOptions}
                  />
                </Space>
              </Col>
              <Col span={6}>
                <Space style={{ paddingTop: '22px' }}>
                  <Button
                    icon={<SearchOutlined />}
                    onClick={() => applyFilters()}
                    type="primary"
                    ghost
                  >
                    搜索
                  </Button>
                  <Button onClick={resetFilters}>
                    重置
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* 根据视图模式渲染不同内容 */}
        {viewMode === ViewMode.TREE ? (
          // 树型视图
                     <Card>
             <div style={{ marginBottom: 16 }}>
               <span style={{ fontWeight: 'bold', color: theme === 'dark' ? '#ffffff' : '#000000' }}>权限树结构</span>
               <span style={{ marginLeft: 8, color: theme === 'dark' ? '#999999' : '#999' }}>
                 显示权限的层级关系：模块 → 页面 → 操作/按钮
               </span>
             </div>
            {permissionTree.length > 0 ? (
              <Tree
                treeData={convertToTreeData(permissionTree)}
                defaultExpandAll
                showLine={{ showLeafIcon: false }}
                style={{ 
                  background: theme === 'dark' ? '#1f1f1f' : '#fafafa',
                  color: theme === 'dark' ? '#ffffff' : '#000000',
                  padding: '16px',
                  borderRadius: '6px',
                  border: theme === 'dark' ? '1px solid #434343' : '1px solid #d9d9d9'
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Title level={4} type="secondary">暂无权限数据</Title>
                <p>请先执行数据库迁移来初始化权限树结构</p>
              </div>
            )}
          </Card>
        ) : (
          // 表格视图
          <Table
            columns={columns}
            dataSource={permissions}
            loading={loading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
                fetchPermissions(page, size || 10, filters.name, filters.category, filters.type);
              },
            }}
          />
        )}

        {/* 创建权限模态框 */}
        <Modal
          title="新建权限"
          open={isCreateModalVisible}
          onCancel={() => {
            setIsCreateModalVisible(false);
            createForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreate}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="code"
                  label="权限代码"
                  rules={[
                    { required: true, message: '请输入权限代码' },
                    { pattern: /^[a-z0-9_.]+$/, message: '权限代码只能包含小写字母、数字、下划线和点' }
                  ]}
                >
                  <Input placeholder="如: user.view" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="权限名称"
                  rules={[{ required: true, message: '请输入权限名称' }]}
                >
                  <Input placeholder="如: 查看用户" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="权限分类"
                  rules={[{ required: true, message: '请选择权限分类' }]}
                >
                  <Select placeholder="请选择分类">
                    <Option value="dashboard">仪表盘</Option>
                    <Option value="user">用户管理</Option>
                    <Option value="role">角色管理</Option>
                    <Option value="permission">权限管理</Option>
                    <Option value="api">API管理</Option>
                    <Option value="log">日志管理</Option>
                    <Option value="system">系统管理</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="type"
                  label="权限类型"
                  rules={[{ required: true, message: '请选择权限类型' }]}
                >
                  <Select placeholder="请选择类型">
                    <Option value="module">模块权限</Option>
                    <Option value="page">页面权限</Option>
                    <Option value="action">操作权限</Option>
                    <Option value="button">按钮权限</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="权限描述"
            >
              <Input.TextArea rows={3} placeholder="描述权限的具体功能" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setIsCreateModalVisible(false);
                  createForm.resetFields();
                }}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  创建
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 编辑权限模态框 */}
        <Modal
          title="编辑权限"
          open={isEditModalVisible}
          onCancel={() => {
            setIsEditModalVisible(false);
            setEditingPermission(null);
            editForm.resetFields();
          }}
          footer={null}
          width={800}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEdit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="code"
                  label="权限代码"
                  rules={[
                    { required: true, message: '请输入权限代码' },
                    { pattern: /^[a-z0-9_.]+$/, message: '权限代码只能包含小写字母、数字、下划线和点' }
                  ]}
                >
                  <Input placeholder="如: user.view" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="权限名称"
                  rules={[{ required: true, message: '请输入权限名称' }]}
                >
                  <Input placeholder="如: 查看用户" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="权限分类"
                  rules={[{ required: true, message: '请选择权限分类' }]}
                >
                  <Select placeholder="请选择分类">
                    <Option value="dashboard">仪表盘</Option>
                    <Option value="user">用户管理</Option>
                    <Option value="role">角色管理</Option>
                    <Option value="permission">权限管理</Option>
                    <Option value="api">API管理</Option>
                    <Option value="log">日志管理</Option>
                    <Option value="system">系统管理</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="type"
                  label="权限类型"
                  rules={[{ required: true, message: '请选择权限类型' }]}
                >
                  <Select placeholder="请选择类型">
                    <Option value="module">模块权限</Option>
                    <Option value="page">页面权限</Option>
                    <Option value="action">操作权限</Option>
                    <Option value="button">按钮权限</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            {/* 层级结构配置 */}
            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fafafa',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#303030' : '#d9d9d9'}`
            }}>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                color: theme === 'dark' ? '#ffffff' : '#000000',
                fontSize: '14px',
                fontWeight: 500
              }}>
                层级结构配置
              </h4>
              <Row gutter={16}>
                <Col span={8}>
                                    <Form.Item
                    name="parent_id"
                    label="父权限"
                    tooltip="选择上级权限，留空表示顶级权限。禁用选项表示不能选择（防止循环引用）"
                    extra={
                      <div style={{ 
                        fontSize: '12px', 
                        color: theme === 'dark' ? '#888' : '#666',
                        marginTop: '4px'
                      }}>
                        💡 提示：树型结构与外部权限树保持一致，显示完整层级关系
                      </div>
                    }
                  >
                    <Select 
                      placeholder="🔍 搜索或选择父权限（留空=顶级权限）" 
                      allowClear
                      showSearch
                      style={{ width: '100%' }}
                      dropdownStyle={{ 
                        maxHeight: 450, 
                        overflow: 'auto',
                        border: `1px solid ${theme === 'dark' ? '#434343' : '#d9d9d9'}`
                      }}
                      filterOption={(input, option) => {
                        if (!option?.title) return false;
                        const title = String(option.title);
                        return title?.toLowerCase().includes(input.toLowerCase()) || false;
                      }}
                      optionLabelProp="title"
                      notFoundContent={
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '20px',
                          color: theme === 'dark' ? '#888' : '#666'
                        }}>
                          <span>🔍 未找到匹配的权限</span>
                        </div>
                      }
                    >
                      {permissionTree.length > 0 ? flattenPermissionTree(permissionTree).map(buildParentOption) : (
                        <Option disabled value="">
                          <div style={{ textAlign: 'center', color: '#999' }}>
                            正在加载权限树...
                          </div>
                        </Option>
                      )}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="level"
                    label="权限层级"
                    tooltip="权限在树中的层级，0为顶级"
                    rules={[{ required: true, message: '请输入权限层级' }]}
                  >
                    <InputNumber 
                      min={0} 
                      max={5} 
                      placeholder="0-5" 
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="sort"
                    label="排序"
                    tooltip="同级权限的排序，数字越小越靠前"
                    rules={[{ required: true, message: '请输入排序值' }]}
                  >
                    <InputNumber 
                      min={0} 
                      placeholder="排序值" 
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="icon"
                    label="图标"
                    tooltip="权限显示的图标名称"
                  >
                    <Input placeholder="如: UserOutlined" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="path"
                    label="权限路径"
                    tooltip="权限的完整路径"
                  >
                    <Input placeholder="如: /user/view" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="权限状态"
                  >
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <Form.Item
                        name="is_leaf"
                        valuePropName="checked"
                        style={{ margin: 0 }}
                      >
                        <Checkbox>叶子节点</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name="is_enabled"
                        valuePropName="checked"
                        style={{ margin: 0 }}
                      >
                        <Checkbox>启用状态</Checkbox>
                      </Form.Item>
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </div>
            
            <Form.Item
              name="description"
              label="权限描述"
            >
              <Input.TextArea rows={3} placeholder="描述权限的具体功能" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setIsEditModalVisible(false);
                  setEditingPermission(null);
                  editForm.resetFields();
                }}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  更新
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 权限详情模态框 */}
        <Modal
          title="权限详情"
          open={isDetailModalVisible}
          onCancel={() => {
            setIsDetailModalVisible(false);
            setDetailPermission(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setIsDetailModalVisible(false);
              setDetailPermission(null);
            }}>
              关闭
            </Button>
          ]}
          width={600}
        >
          {detailPermission && (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">
                {detailPermission.id}
              </Descriptions.Item>
              <Descriptions.Item label="权限代码">
                <code style={{ 
                  background: theme === 'dark' ? '#2f2f2f' : '#f5f5f5', 
                  color: theme === 'dark' ? '#ffffff' : '#000000',
                  padding: '2px 6px', 
                  borderRadius: '4px' 
                }}>
                  {detailPermission.code}
                </code>
              </Descriptions.Item>
              <Descriptions.Item label="权限名称">
                {detailPermission.name}
              </Descriptions.Item>
              <Descriptions.Item label="权限分类">
                <Tag color={getCategoryColor(detailPermission.category)}>
                  {detailPermission.category}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="权限类型">
                <Tag color={getTypeColor(detailPermission.type)}>
                  {getTypeText(detailPermission.type)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="权限描述">
                {detailPermission.description || '暂无描述'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(detailPermission.created_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {new Date(detailPermission.updated_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default PermissionManagement; 